import { GroupingRule, type ArdoqId } from '@ardoq/api-types';
import type { GraphModelShape, SimpleReferenceModel } from '@ardoq/data-model';
import {
  ClonedComponentsMap,
  ComponentGroupsMap,
  ProcessedReferences,
  LoadedGraphWithViewpointMode,
} from '../types';
import { uniq } from 'lodash';
import { TraversalAndGroupingResult } from '../traversal/types';
import { GraphInterface } from '../graphInterface/graphInterface';
import { returnTrue } from '@ardoq/common-helpers';
import { GROUP_BY_PARENT_ALL } from '../consts';

const getParentAndChild = (parentChildReferenceId: string) => {
  const [sourceId, targetId] = parentChildReferenceId.split('-');
  return { sourceId, targetId };
};

export const getOnlyConnectedComponents = (
  componentIds: ArdoqId[],
  traversedReferenceIds: ArdoqId[],
  parentReferenceIds: Set<ArdoqId>,
  referenceMap: Map<ArdoqId, SimpleReferenceModel>
) => {
  const linkedComponentIds = new Set(
    traversedReferenceIds.flatMap(referenceId => {
      const isParentChildReference = parentReferenceIds.has(referenceId);
      const { sourceId, targetId } = isParentChildReference
        ? getParentAndChild(referenceId)
        : referenceMap.get(referenceId)!;
      return [sourceId, targetId];
    })
  );
  return componentIds.filter(componentId =>
    linkedComponentIds.has(componentId)
  );
};

type GetResultingReferenceListArgs = {
  /**
   * The list of reference ids which have been traversed according to the
   * traversal configuration.
   */
  traversedReferenceIds: string[];
  /**
   * The references which have been used for the grouping, they will not
   * be exposed as references in the result.
   */
  processedReferences: ProcessedReferences;
  /**
   * Parent child relations are not modeled as references in Ardoq, but for
   * graph views it makes sense to treat them as such, so we need to maintain
   * them separately.
   */
  parentReferenceIds: Set<string>;
  /**
   * Components are potentially used as group containers in the final result,
   * so the mapping of component ids to group ids is needed to point
   * the source and target to the correct graph item in the final result, e.g.
   * potentially to the according group.
   */
  componentGroupsMap: ComponentGroupsMap;
  /**
   * Has the same purpose as the componentGroupsMap except it's the map of
   * all the clones as a result of the grouping, meaning a given reference
   * has to connect the actual source and target, but also all their clones.
   */
  clonedComponentsMap: ClonedComponentsMap;
  /**
   * The input graph to read out source and target for a given reference.
   */
  graph: GraphModelShape;
};

export const getResultingReferenceList = ({
  traversedReferenceIds,
  processedReferences,
  parentReferenceIds,
  componentGroupsMap,
  clonedComponentsMap,
  graph,
}: GetResultingReferenceListArgs) =>
  uniq(traversedReferenceIds)
    .filter(referenceId => !processedReferences.has(referenceId))
    .flatMap(referenceId => {
      const isParentChildReference = parentReferenceIds.has(referenceId);
      const { sourceId, targetId } = isParentChildReference
        ? getParentAndChild(referenceId)
        : graph.referenceMap.get(referenceId)!;
      const anySource = componentGroupsMap.get(sourceId) || [
        sourceId,
        ...(clonedComponentsMap.get(sourceId) || []),
      ];
      const anyTarget = componentGroupsMap.get(targetId) || [
        targetId,
        ...(clonedComponentsMap.get(targetId) || []),
      ];
      let clonedReferenceIndex = -1;
      return anySource.flatMap(anySourceId =>
        anyTarget.map(anyTargetId => {
          const suffix =
            ++clonedReferenceIndex === 0
              ? ''
              : `-clone-${clonedReferenceIndex}`;

          return {
            id: `${referenceId}${suffix}`,
            modelId: referenceId,
            sourceId: anySourceId,
            targetId: anyTargetId,
            isParentChildReference,
          };
        })
      );
    });

export const getEmptyTraversalAndGroupingResult =
  (): TraversalAndGroupingResult => ({
    groupMap: new Map(),
    rootGroups: [],
    componentMap: new Map(),
    referenceList: [],
    errors: [],
    hasClones: false,
  });

const getGroupContainerCandidatesForTraversalHierarchy = (
  graphInterface: GraphInterface,
  loadedGraph: LoadedGraphWithViewpointMode
) =>
  new Set(
    loadedGraph.hierarchyDefinition.flatMap(({ startSetResult }) =>
      Array.from(startSetResult).flatMap(id =>
        graphInterface.getComponentAncestryIds(id)
      )
    )
  );

export const getCanBeUsedAsGroup = (
  graphInterface: GraphInterface,
  loadedGraph: LoadedGraphWithViewpointMode,
  hasValidUserDefinedGroups: boolean
) => {
  if (hasValidUserDefinedGroups) {
    return returnTrue;
  }
  const groupContainerCandidates =
    getGroupContainerCandidatesForTraversalHierarchy(
      graphInterface,
      loadedGraph
    );

  return (id: ArdoqId) => groupContainerCandidates.has(id);
};

/**
 * Requirement for hierarchical views: if the user has not provided a grouping rule, group by parent all
 */
export const getAppliedGroups = (
  supportedUserDefinedGroupingRules: GroupingRule[]
): GroupingRule[] =>
  supportedUserDefinedGroupingRules.length > 0
    ? supportedUserDefinedGroupingRules
    : [GROUP_BY_PARENT_ALL];
